/**
 * NAD Formula Section Component
 * Handles animations and interactions for the formula benefits section
 */

export class NADFormulaSection {
  private element: HTMLElement;
  private benefits: NodeListOf<HTMLElement>;
  private observer: IntersectionObserver | null = null;

  constructor(element: HTMLElement) {
    this.element = element;
    this.benefits = this.element.querySelectorAll('.nad-formula-section__benefit');
    this.init();
  }

  private init(): void {
    console.log('NAD Formula Section initialized');
    this.setupIntersectionObserver();
    this.bindEvents();
  }

  private setupIntersectionObserver(): void {
    // Only setup observer if user prefers motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const options: IntersectionObserverInit = {
      threshold: 0.2,
      rootMargin: '0px 0px -50px 0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const benefit = entry.target as HTMLElement;
          benefit.classList.add('nad-formula-section__benefit--visible');
          
          // Unobserve after animation triggers
          this.observer?.unobserve(benefit);
        }
      });
    }, options);

    // Observe all benefits
    this.benefits.forEach((benefit) => {
      this.observer?.observe(benefit);
    });
  }

  private bindEvents(): void {
    // Handle window resize for responsive adjustments
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  private handleResize(): void {
    // Debounce resize handler
    if (this.resizeTimeout !== null) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.adjustLayoutForViewport();
    }, 250);
  }

  private resizeTimeout: NodeJS.Timeout | null = null;

  private adjustLayoutForViewport(): void {
    // Add any viewport-specific adjustments here
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      this.element.classList.add('nad-formula-section--mobile');
    } else {
      this.element.classList.remove('nad-formula-section--mobile');
    }
  }

  public destroy(): void {
    // Clean up observers
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Remove event listeners
    window.removeEventListener('resize', this.handleResize.bind(this));

    // Clear timeout
    if (this.resizeTimeout !== null) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }

    console.log('NAD Formula Section destroyed');
  }

  // Static method for automatic initialization
  static initializeAll(): void {
    const elements = document.querySelectorAll<HTMLElement>('.nad-formula-section');
    elements.forEach((element) => {
      if (!(element as any)._nadFormulaSectionInstance) {
        (element as any)._nadFormulaSectionInstance = new NADFormulaSection(element);
      }
    });
  }

  // Static method for cleanup
  static destroyAll(): void {
    const elements = document.querySelectorAll<HTMLElement>('.nad-formula-section');
    elements.forEach((element) => {
      const instance = (element as any)._nadFormulaSectionInstance;
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
        delete (element as any)._nadFormulaSectionInstance;
      }
    });
  }
}
